import { c as _c } from "react/compiler-runtime";
import { useCache } from '@effektify/cache';
import { DATA_QUERY_KEYS } from '@effektify/data-provider';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useCallback, useMemo } from 'react';
import { QUERY_KEYS } from 'src/constants/queries';
import { ReportItem } from 'src/api/responses';
import { Teams } from 'src/api/Teams';
import { UpdateGoalPayload } from 'src/api/requests';
import useApi from './useApi';
import useAuth from './useAuth';
import useTeams from './useTeams';
/**
 *
 * Teams that has available report definitions are
 * Grow 1 (01JY0V50QTT1GJ1Q3HQE6FQ1N8 )
 * Grow 2 (01JY0V5ZQNSDP2A5BF0SWXZMJ5 )
 * Grow 3 (01JY0V73GC5GJAK300054WG3WA )
 */
// Pure and free of hook state, so they live at module scope: that makes them stable for the
// consumers that destructure them off this hook, with no memoization needed.
const getReportUrl = (teamId: string, reportId: string) => `${process.env.NEXT_PUBLIC_API_URL}/teams/${teamId}/reports/${reportId}`;
const getSharedReportUrl = (reportId: string) => `${process.env.NEXT_PUBLIC_API_URL}/reports/${reportId}`;
const useReports = () => {
  const $ = _c(55);
  const {
    resetDataOnly,
    updateGoals,
    getGoals
  } = useCache();
  const queryClient = useQueryClient();
  const reportClient = useApi(Teams);
  const {
    isAuthenticated
  } = useAuth();
  const {
    currentTeam
  } = useTeams();
  const [currentReport, setCurrentReport] = useState(null);
  const teamId = currentTeam?.id || "";
  let t0;
  if ($[0] !== teamId) {
    t0 = report => ({
      ...report,
      url: getReportUrl(teamId, report.id)
    });
    $[0] = teamId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const getReportWithRoute = t0;
  let t1;
  if ($[2] !== teamId) {
    t1 = [QUERY_KEYS.REPORTS.LIST, teamId];
    $[2] = teamId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== reportClient || $[5] !== teamId) {
    t2 = async () => {
      const response = await reportClient.getReports(teamId);
      const {
        data
      } = response;
      if (!data) {
        throw new Error("Invalid team response format");
      }
      return data;
    };
    $[4] = reportClient;
    $[5] = teamId;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const t3 = !!teamId && !!isAuthenticated;
  let t4;
  if ($[7] !== t1 || $[8] !== t2 || $[9] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3,
      staleTime: 900000
    };
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const {
    data: data_0,
    isLoading,
    refetch: fetchReportList
  } = useQuery(t4);
  let t5;
  if ($[11] !== reportClient || $[12] !== teamId) {
    t5 = t6 => {
      const {
        goalType,
        body
      } = t6;
      return reportClient.updateGoal(teamId, goalType, body);
    };
    $[11] = reportClient;
    $[12] = teamId;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== getGoals || $[15] !== queryClient || $[16] !== updateGoals) {
    t6 = (_, t7) => {
      const {
        goalType: goalType_0,
        body: body_0
      } = t7;
      const {
        data: existingGoals
      } = getGoals();
      const existingGoal = existingGoals?.[goalType_0];
      updateGoals({
        [goalType_0]: {
          type: existingGoal?.type ?? "integer",
          benchmark: existingGoal?.benchmark ?? 0,
          goal: body_0.goal,
          description: body_0.description
        }
      });
      queryClient.invalidateQueries({
        queryKey: [DATA_QUERY_KEYS.REPORT_DATA]
      });
    };
    $[14] = getGoals;
    $[15] = queryClient;
    $[16] = updateGoals;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== t5 || $[19] !== t6) {
    t7 = {
      mutationFn: t5,
      onSuccess: t6
    };
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const {
    mutateAsync: updateGoalMutation
  } = useMutation(t7);
  let t8;
  if ($[21] !== reportClient || $[22] !== teamId) {
    t8 = t9 => {
      const {
        goalType: goalType_1,
        groupId
      } = t9;
      return reportClient.deleteGoal(teamId, goalType_1, groupId ? {
        groupId
      } : undefined);
    };
    $[21] = reportClient;
    $[22] = teamId;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== getGoals || $[25] !== queryClient || $[26] !== updateGoals) {
    t9 = (__0, t10) => {
      const {
        goalType: goalType_2
      } = t10;
      const {
        data: existingGoals_0
      } = getGoals();
      const existingGoal_0 = existingGoals_0?.[goalType_2];
      updateGoals({
        [goalType_2]: {
          type: existingGoal_0?.type ?? "integer",
          benchmark: existingGoal_0?.benchmark ?? 0,
          goal: null,
          description: null
        }
      });
      queryClient.invalidateQueries({
        queryKey: [DATA_QUERY_KEYS.REPORT_DATA]
      });
    };
    $[24] = getGoals;
    $[25] = queryClient;
    $[26] = updateGoals;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== t8 || $[29] !== t9) {
    t10 = {
      mutationFn: t8,
      onSuccess: t9
    };
    $[28] = t8;
    $[29] = t9;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  const {
    mutateAsync: deleteGoalMutation
  } = useMutation(t10);
  let t11;
  if ($[31] !== updateGoalMutation) {
    t11 = (goalType_3, body_1) => updateGoalMutation({
      goalType: goalType_3,
      body: body_1
    });
    $[31] = updateGoalMutation;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  const updateGoal = t11;
  let t12;
  if ($[33] !== deleteGoalMutation) {
    t12 = (goalType_4, options) => deleteGoalMutation({
      goalType: goalType_4,
      groupId: options?.groupId
    });
    $[33] = deleteGoalMutation;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  const deleteGoal = t12;
  let t13;
  if ($[35] !== data_0 || $[36] !== getReportWithRoute) {
    t13 = data_0?.[0] ? getReportWithRoute(data_0[0]) : null;
    $[35] = data_0;
    $[36] = getReportWithRoute;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  const dashboard = t13;
  let t14;
  if ($[38] !== data_0) {
    t14 = data_0 ?? [];
    $[38] = data_0;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  let t15;
  if ($[40] !== t14) {
    t15 = t14.slice(1);
    $[40] = t14;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  const reportList = t15;
  let t16;
  if ($[42] !== getReportWithRoute || $[43] !== reportList || $[44] !== resetDataOnly) {
    t16 = async reportId => {
      const report_0 = reportList.find(r => r.id === reportId);
      if (report_0) {
        await resetDataOnly();
        setCurrentReport(getReportWithRoute(report_0));
      }
    };
    $[42] = getReportWithRoute;
    $[43] = reportList;
    $[44] = resetDataOnly;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  const updateCurrentReport = t16;
  let t17;
  if ($[46] !== currentReport || $[47] !== dashboard || $[48] !== deleteGoal || $[49] !== fetchReportList || $[50] !== isLoading || $[51] !== reportList || $[52] !== updateCurrentReport || $[53] !== updateGoal) {
    t17 = {
      fetchReportList,
      dashboard,
      currentReport,
      updateCurrentReport,
      reportList,
      isLoading,
      getReportUrl,
      getSharedReportUrl,
      updateGoal,
      deleteGoal
    };
    $[46] = currentReport;
    $[47] = dashboard;
    $[48] = deleteGoal;
    $[49] = fetchReportList;
    $[50] = isLoading;
    $[51] = reportList;
    $[52] = updateCurrentReport;
    $[53] = updateGoal;
    $[54] = t17;
  } else {
    t17 = $[54];
  }
  return t17;
};
export default useReports;